import { ChangeEvent, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Enterprise, updateEnterprise } from "../../lib/api";
import { MY_ENTERPRISE_KEY } from "../../lib/useEnterpriseBranding";
import { Card, ErrorNote } from "./ui";

/** Enterprise logo manager. Shown on the Enterprise admin page to
 * agrilens_super and the enterprise_admin. Uploading sets the logo
 * that every state/district head under this enterprise then sees in
 * their header; clearing removes it.
 *
 * The file is read as a base64 data URL in the browser and sent to
 * PATCH /api/enterprises/:id as `logo_base64`; the backend uploads to
 * blob storage and stores the resulting URL. When blob storage isn't
 * configured the backend soft-fails and returns a `logo_warning`,
 * which we surface honestly rather than pretending it worked. */
const MAX_BYTES = 2 * 1024 * 1024; // 2 MB — logos are small; guard uploads.
const ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

export function EnterpriseBrandingCard({
  enterprise,
}: {
  enterprise: Enterprise;
}) {
  const qc = useQueryClient();
  const [preview, setPreview] = useState<string | null>(null);
  const [pendingB64, setPendingB64] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["enterprise", enterprise.id] });
    qc.invalidateQueries({ queryKey: MY_ENTERPRISE_KEY });
  };

  const saveMut = useMutation({
    mutationFn: (logo_base64: string | null) =>
      updateEnterprise(enterprise.id, { logo_base64 }),
    onSuccess: (resp) => {
      setWarning(resp.logo_warning ?? null);
      setPreview(null);
      setPendingB64(null);
      invalidate();
    },
  });

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    setLocalError(null);
    setWarning(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setLocalError(
        `That image is ${(file.size / 1024 / 1024).toFixed(1)} MB — please ` +
          "use one under 2 MB.",
      );
      return;
    }
    try {
      const dataUrl = await readAsDataUrl(file);
      setPreview(dataUrl);
      setPendingB64(dataUrl);
    } catch {
      setLocalError("Could not read that file. Try a different image.");
    }
  };

  const current = enterprise.logo_url;

  return (
    <Card title="Enterprise branding">
      <p className="text-xs text-gray-500 mb-3">
        Upload your organisation's logo. State and district heads under
        this enterprise see it in their portal header.
      </p>

      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
          {preview ? (
            <img
              src={preview}
              alt="New logo preview"
              className="h-full w-full object-contain"
            />
          ) : current ? (
            <img
              src={current}
              alt={`${enterprise.name} logo`}
              className="h-full w-full object-contain"
            />
          ) : (
            <span className="text-[10px] text-gray-400 text-center px-1">
              No logo
            </span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label className="inline-block">
            <span className="sr-only">Choose logo image</span>
            <input
              type="file"
              accept={ACCEPT}
              onChange={onPick}
              className="block text-sm text-gray-600 file:mr-3 file:rounded file:border-0 file:bg-agrilens-green file:px-3 file:py-1.5 file:text-white file:text-sm hover:file:bg-agrilens-green-dark"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!pendingB64 || saveMut.isPending}
              onClick={() => pendingB64 && saveMut.mutate(pendingB64)}
              className="bg-agrilens-green text-white text-sm font-medium px-4 py-1.5 rounded hover:bg-agrilens-green-dark disabled:opacity-40"
            >
              {saveMut.isPending && pendingB64 ? "Uploading…" : "Save logo"}
            </button>
            {current && (
              <button
                type="button"
                disabled={saveMut.isPending}
                onClick={() => saveMut.mutate(null)}
                className="text-sm text-gray-600 border border-gray-300 px-3 py-1.5 rounded hover:bg-gray-50 disabled:opacity-40"
              >
                {saveMut.isPending && !pendingB64 ? "Clearing…" : "Clear"}
              </button>
            )}
          </div>
        </div>
      </div>

      {localError && (
        <div className="mt-3 text-xs text-red-700 bg-red-50 rounded p-2">
          {localError}
        </div>
      )}
      {saveMut.isError && (
        <div className="mt-3">
          <ErrorNote error={saveMut.error} />
        </div>
      )}
      {warning && (
        <div className="mt-3 text-xs text-yellow-800 bg-yellow-50 rounded p-2">
          Saved, but the image couldn't be stored: {warning}. Check that
          blob storage is configured on the backend.
        </div>
      )}
    </Card>
  );
}
